import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { EsscoCatalogProvider } from './contexts/EsscoCatalogContext';
import { ToastProvider } from './contexts/ToastContext';
import LoginPage from './pages/Login';
import Navigation, { useSidebarCollapsed } from './components/Navigation';
import { ErrorBoundary } from './components/ErrorBoundary';
import * as navStyles from './components/Navigation.css.ts';
import { BackBar, BackBarProvider } from './components/BackBar';
import { lazyWithRetry } from './utils/chunkLoadRecovery';

// Route-level code splitting: each page loads on first navigation
const ProjectCreatePage = lazyWithRetry(() => import('./pages/ProjectCreate'));
const ProjectsListPage = lazyWithRetry(() => import('./pages/ProjectsList'));
const ProjectViewPage = lazyWithRetry(() => import('./pages/ProjectView'));
const ProjectDetailsPage = lazyWithRetry(() => import('./pages/ProjectDetails'));
const EstimatesListPage = lazyWithRetry(() => import('./pages/EstimatesList'));
const EstimateCreatePage = lazyWithRetry(() => import('./pages/EstimateCreate'));
const EstimateViewPage = lazyWithRetry(() => import('./pages/EstimateView'));
const EsscoInventoryPage = lazyWithRetry(() => import('./pages/EsscoInventory'));
const OrdersListPage = lazyWithRetry(() => import('./pages/OrdersList'));
const OrderCreatePage = lazyWithRetry(() => import('./pages/OrderCreate'));
const OrderViewPage = lazyWithRetry(() => import('./pages/OrderView'));
const OrderRecordInvoicePage = lazyWithRetry(() => import('./pages/OrderRecordInvoice'));
const EmployeesPage = lazyWithRetry(() => import('./pages/Employees'));
const WorkerProfilePage = lazyWithRetry(() => import('./pages/WorkerProfile'));
const CalendarPage = lazyWithRetry(() => import('./pages/Calendar'));
const FieldSubmissionsPage = lazyWithRetry(() => import('./pages/FieldSubmissions'));
const AssignmentDetailPage = lazyWithRetry(() => import('./pages/AssignmentDetailPage'));
const FloorPlanCalculatorPage = lazyWithRetry(() => import('./pages/FloorPlanCalculator'));
const sowEnabled = import.meta.env.VITE_ESTIMATOR_ENABLED === 'true';
const SowWorkbenchPage = sowEnabled ? lazyWithRetry(() => import('./pages/SowWorkbench')) : null;
const PipelinePage = lazyWithRetry(() => import('./pages/Pipeline'));
const DealDetailPage = lazyWithRetry(() => import('./pages/DealDetail'));
const TasksPage = lazyWithRetry(() => import('./pages/Tasks'));
const ContactsPage = lazyWithRetry(() => import('./pages/Contacts'));
const RelationshipsPage = lazyWithRetry(() => import('./pages/Relationships'));
const CompanyDetailPage = lazyWithRetry(() => import('./pages/CompanyDetail'));
const ContactDetailPage = lazyWithRetry(() => import('./pages/ContactDetail'));
const InspectionCreatePage = lazyWithRetry(() => import('./pages/InspectionCreate'));
const InspectionViewPage = lazyWithRetry(() => import('./pages/InspectionView'));
const AiaContractPage = lazyWithRetry(() => import('./pages/AiaContract'));
const ProjectPaymentAppsListPage = lazyWithRetry(() => import('./pages/ProjectPaymentAppsList'));
const ProjectSupplyOrdersListPage = lazyWithRetry(() => import('./pages/ProjectSupplyOrdersList'));
const ProjectEstimatesListPage = lazyWithRetry(() => import('./pages/ProjectEstimatesList'));
const ProjectPaymentAppFormPage = lazyWithRetry(() => import('./pages/ProjectPaymentAppForm'));
const ProjectPaymentAppViewPage = lazyWithRetry(() => import('./pages/ProjectPaymentAppView'));
const ProjectActualsPage = lazyWithRetry(() => import('./pages/ProjectActuals'));
const ProjectPnlPage = lazyWithRetry(() => import('./pages/ProjectPnl'));
const LabSampleResultViewPage = lazyWithRetry(() => import('./pages/LabSampleResultView'));
const ReceiptsListPage = lazyWithRetry(() => import('./pages/ReceiptsList'));
const ReceiptFormPage = lazyWithRetry(() => import('./pages/ReceiptForm'));
const ReceiptBulkReviewPage = lazyWithRetry(() => import('./pages/ReceiptBulkReview'));
const ReceiptBulkUploadPage = lazyWithRetry(() => import('./pages/ReceiptBulkUpload'));
const ReceiptViewPage = lazyWithRetry(() => import('./pages/ReceiptView'));
const DocsPage = lazyWithRetry(() => import('./pages/Docs'));
const BillingQueuePage = lazyWithRetry(() => import('./pages/BillingQueue'));
const SiteScoutListPage = lazyWithRetry(() => import('./pages/SiteScoutList'));
const DevHealthPage = lazyWithRetry(() => import('./pages/DevHealth'));
const SiteScoutJobPage = lazyWithRetry(() => import('./pages/SiteScoutJob'));
const SiteScoutShareViewPage = lazyWithRetry(() => import('./pages/SiteScoutShareView'));

function Protected({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <div>Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <Router>
          <BackBarProvider>
            <div className="app">
              <AppContent />
            </div>
          </BackBarProvider>
        </Router>
      </ToastProvider>
    </AuthProvider>
  );
}

function AppContent() {
  const { user } = useAuth();
  const sidebarCollapsed = useSidebarCollapsed();
  
  return (
    <EsscoCatalogProvider>
      <div className={navStyles.appLayout}>
        {user && <Navigation />}
        <div className={`${navStyles.mainContent} ${user && sidebarCollapsed ? navStyles.mainContentCollapsed : ''}`} style={!user ? { marginLeft: 0 } : undefined}>
          {user && <BackBar />}
          <ErrorBoundary>
          <Suspense fallback={<div style={{ padding: 24 }}>Loading...</div>}>
          <Routes>
            <Route path="/" element={<Navigate to="/projects" replace />} />
            <Route
              path="/pipeline"
              element={
                <Protected>
                  <PipelinePage />
                </Protected>
              }
            />
            <Route
              path="/deals/:id"
              element={
                <Protected>
                  <DealDetailPage />
                </Protected>
              }
            />
            <Route
              path="/tasks"
              element={
                <Protected>
                  <TasksPage />
                </Protected>
              }
            />
            <Route
              path="/contacts"
              element={
                <Protected>
                  <ContactsPage />
                </Protected>
              }
            />
            <Route
              path="/contacts/:id"
              element={
                <Protected>
                  <ContactDetailPage />
                </Protected>
              }
            />
            <Route
              path="/relationships"
              element={
                <Protected>
                  <RelationshipsPage />
                </Protected>
              }
            />
            <Route
              path="/companies/:id"
              element={
                <Protected>
                  <CompanyDetailPage />
                </Protected>
              }
            />
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/dev-health"
              element={
                <Protected>
                  <DevHealthPage />
                </Protected>
              }
            />
            <Route
              path="/projects"
              element={
                <Protected>
                  <ProjectsListPage />
                </Protected>
              }
            />
            <Route
              path="/projects/create"
              element={
                <Protected>
                  <ProjectCreatePage />
                </Protected>
              }
            />
            <Route
              path="/projects/:id"
              element={
                <Protected>
                  <ProjectViewPage />
                </Protected>
              }
            />
            <Route
              path="/projects/:id/details"
              element={
                <Protected>
                  <ProjectDetailsPage />
                </Protected>
              }
            />
            <Route
              path="/projects/:id/actuals"
              element={
                <Protected>
                  <ProjectActualsPage />
                </Protected>
              }
            />
            <Route
              path="/projects/:id/pnl"
              element={
                <Protected>
                  <ProjectPnlPage />
                </Protected>
              }
            />
            <Route
              path="/projects/:id/edit"
              element={
                <Protected>
                  <ProjectCreatePage editMode />
                </Protected>
              }
            />
            <Route
              path="/projects/:projectId/orders"
              element={
                <Protected>
                  <ProjectSupplyOrdersListPage />
                </Protected>
              }
            />
            <Route
              path="/projects/:projectId/receipts"
              element={
                <Protected>
                  <ReceiptsListPage />
                </Protected>
              }
            />
            <Route
              path="/projects/:projectId/receipts/bulk"
              element={
                <Protected>
                  <ReceiptBulkUploadPage />
                </Protected>
              }
            />
            <Route
              path="/projects/:projectId/receipts/batches/:batchId/review"
              element={
                <Protected>
                  <ReceiptBulkReviewPage />
                </Protected>
              }
            />
            <Route
              path="/projects/:projectId/estimates"
              element={
                <Protected>
                  <ProjectEstimatesListPage />
                </Protected>
              }
            />
            <Route
              path="/projects/:projectId/apps"
              element={
                <Protected>
                  <ProjectPaymentAppsListPage />
                </Protected>
              }
            />
            <Route
              path="/projects/:projectId/apps/new"
              element={
                <Protected>
                  <ProjectPaymentAppFormPage />
                </Protected>
              }
            />
            <Route
              path="/billing-contracts/:id"
              element={<Protected><AiaContractPage /></Protected>}
            />
            <Route
              path="/project-payment-apps/:id"
              element={
                <Protected>
                  <ProjectPaymentAppViewPage />
                </Protected>
              }
            />
            <Route
              path="/project-payment-apps/:id/edit"
              element={
                <Protected>
                  <ProjectPaymentAppFormPage />
                </Protected>
              }
            />
            <Route
              path="/lab-sample-results/:id"
              element={
                <Protected>
                  <LabSampleResultViewPage />
                </Protected>
              }
            />
            <Route
              path="/projects/:projectId/floor-plans/new"
              element={
                <Protected>
                  <FloorPlanCalculatorPage />
                </Protected>
              }
            />
            <Route
              path="/floor-plans/:id"
              element={
                <Protected>
                  <FloorPlanCalculatorPage />
                </Protected>
              }
            />
            {sowEnabled && SowWorkbenchPage && (
              <Route
                path="/sow"
                element={
                  <Protected>
                    <SowWorkbenchPage />
                  </Protected>
                }
              />
            )}
            <Route
              path="/estimates"
              element={
                <Protected>
                  <EstimatesListPage />
                </Protected>
              }
            />
            <Route
              path="/estimates/new"
              element={
                <Protected>
                  <EstimateCreatePage />
                </Protected>
              }
            />
            <Route
              path="/estimates/:id"
              element={
                <Protected>
                  <EstimateViewPage />
                </Protected>
              }
            />
            <Route
              path="/estimates/:id/edit"
              element={
                <Protected>
                  <EstimateCreatePage editMode />
                </Protected>
              }
            />
            <Route
              path="/inspections/new"
              element={
                <Protected>
                  <InspectionCreatePage />
                </Protected>
              }
            />
            <Route
              path="/inspections/:id"
              element={
                <Protected>
                  <InspectionViewPage />
                </Protected>
              }
            />
            <Route
              path="/inspections/:id/edit"
              element={
                <Protected>
                  <InspectionCreatePage />
                </Protected>
              }
            />
            <Route
              path="/orders"
              element={
                <Protected>
                  <OrdersListPage />
                </Protected>
              }
            />
            <Route
              path="/orders/new"
              element={
                <Protected>
                  <OrderCreatePage />
                </Protected>
              }
            />
            <Route
              path="/orders/:id"
              element={
                <Protected>
                  <OrderViewPage />
                </Protected>
              }
            />
            <Route
              path="/orders/:id/edit"
              element={
                <Protected>
                  <OrderCreatePage editMode />
                </Protected>
              }
            />
            <Route
              path="/orders/:id/invoice"
              element={
                <Protected>
                  <OrderRecordInvoicePage />
                </Protected>
              }
            />
            <Route
              path="/receipts"
              element={
                <Protected>
                  <ReceiptsListPage />
                </Protected>
              }
            />
            <Route
              path="/receipts/new"
              element={
                <Protected>
                  <ReceiptFormPage />
                </Protected>
              }
            />
            <Route
              path="/receipts/:id"
              element={
                <Protected>
                  <ReceiptViewPage />
                </Protected>
              }
            />
            <Route
              path="/receipts/:id/edit"
              element={
                <Protected>
                  <ReceiptFormPage editMode />
                </Protected>
              }
            />
            <Route
              path="/billing"
              element={
                <Protected>
                  <BillingQueuePage />
                </Protected>
              }
            />
            <Route
              path="/inventory"
              element={
                <Protected>
                  <EsscoInventoryPage />
                </Protected>
              }
            />
            <Route
              path="/employees"
              element={
                <Protected>
                  <EmployeesPage />
                </Protected>
              }
            />
            <Route
              path="/employees/workers/:id"
              element={
                <Protected>
                  <WorkerProfilePage />
                </Protected>
              }
            />
            <Route
              path="/calendar"
              element={
                <Protected>
                  <CalendarPage />
                </Protected>
              }
            />
            <Route
              path="/calendar/assignments/:id"
              element={
                <Protected>
                  <AssignmentDetailPage />
                </Protected>
              }
            />
            <Route
              path="/field-submissions"
              element={
                <Protected>
                  <FieldSubmissionsPage />
                </Protected>
              }
            />
            {/* Public: documents page, shareable to employees without login */}
            <Route path="/docs" element={<DocsPage />} />
            <Route
              path="/site-scout"
              element={
                <Protected>
                  <SiteScoutListPage />
                </Protected>
              }
            />
            <Route
              path="/site-scout/:id"
              element={
                <Protected>
                  <SiteScoutJobPage />
                </Protected>
              }
            />
            {/* Public: subcontractor share links, no login */}
            <Route path="/share/site-scout/:token" element={<SiteScoutShareViewPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
          </ErrorBoundary>
        </div>
      </div>
    </EsscoCatalogProvider>
  );
}

export default App;
